import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import Layout from "@/components/Layout";
import { useSEO, SEO_CONFIGS } from "@/hooks/useSEO";
// @ts-nocheck
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import VideoPlayer, { Video } from "@/components/ui/VideoPlayer";
import { ChevronRight, Calendar, Users, ArrowRight, Clock, PackageOpen, FileText, Handshake, Palette, Hammer, Zap, BookOpen, TrendingUp, User, DollarSign, Globe, Award, Shield, Star, MapPin, GlassWater, UserCheck, Map, Apple, Eye, Send, Smile, Loader2, MessageCircle, Target, Heart, Lock, Truck, UserPlus, Gavel } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "@/components/ui/use-toast";
import { useAuth } from "@/providers/AuthProvider";
import { Input } from "@/components/ui/input";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { ScrollArea } from "@/components/ui/scroll-area";
import {
  Carousel,
  CarouselContent,
  CarouselItem,
  CarouselNext,
  CarouselPrevious,
} from "@/components/ui/carousel";
import { AspectRatio } from "@/components/ui/aspect-ratio";
import LiveAuctionStream from "@/components/LiveAuctionStream";
import UpcomingStreamPreview from "@/components/UpcomingStreamPreview";
import LiveStreamSection from "@/components/LiveStreamSection";
import LivingGallery from "@/components/LivingGallery";
import ModernLiveStreamSection from "@/components/ModernLiveStreamSection";
import ModernUpcomingAuctions from "@/components/ModernUpcomingAuctions";
import PastAuctions from "@/components/PastAuctions";
import AuctionLotsCarousel from "@/components/AuctionLotsCarousel";
import WelcomeAuctionBanner from "@/components/WelcomeAuctionBanner";
import AdvancedUpcomingAuctions from "@/components/AdvancedUpcomingAuctions";
import ServicesSection from "@/components/ServicesSection";

import { formatToEST, formatTimeOnlyEST, formatCurrency } from '@/utils/dateUtils';

interface LiveAuction {
  id: string;
  title: string;
  stream_url: string;
  is_live: boolean;
}

interface LiveBroadcast {
  id: string;
  title: string;
  description: string | null;
  youtube_url: string | null;
  is_live: boolean;
  auction_id: string | null;
  current_lot_id: string | null;
}

interface LiveLot {
  id: string;
  lot_number: string;
  starting_bid: number;
  current_bid: number | null;
  artwork: {
    title: string;
    image_url: string | null;
    artist: {
      name: string;
    } | null;
  } | null;
}

interface ChatMessage {
  id: string;
  content: string;
  created_at: string;
  user_id: string;
  profiles: {
    first_name: string | null;
    last_name: string | null;
    paddle_number: number | null;
    use_paddle_for_display: boolean | null;
  } | null;
}

interface BidActivity {
  id: string;
  amount: number;
  created_at: string;
  user_id: string;
  lot_id: string;
  status: string;
  profiles: {
    first_name: string | null;
    last_name: string | null;
    paddle_number: number | null;
    use_paddle_for_display: boolean | null;
  } | null;
}

const Index = () => {
  const { user } = useAuth();
  const [featuredAuctions, setFeaturedAuctions] = useState<any[]>([]);
  const [liveAuction, setLiveAuction] = useState<LiveAuction | null>(null);
  const [loading, setLoading] = useState(true);
  const [featuredBlogPosts, setFeaturedBlogPosts] = useState<any[]>([]);
  
  // Live stream data
  const [liveBroadcast, setLiveBroadcast] = useState<LiveBroadcast | null>(null);
  const [currentLot, setCurrentLot] = useState<LiveLot | null>(null);
  const [upcomingLots, setUpcomingLots] = useState<LiveLot[]>([]);
  const [chatMessages, setChatMessages] = useState<ChatMessage[]>([]);
  const [recentBids, setRecentBids] = useState<BidActivity[]>([]);
  const [viewerCount, setViewerCount] = useState<number>(0);
  const [newChatMessage, setNewChatMessage] = useState("");
  const [streamError, setStreamError] = useState(false);

  // SEO optimization for homepage
  useSEO({
    ...SEO_CONFIGS.homepage,
    canonicalUrl: '/',
    ogImage: '/lovable-uploads/a04e8ead-7f23-41b8-91a5-a2427d0afcb6.png'
  });

  // Fetch live broadcast data
  const fetchLiveBroadcast = async () => {
    try {
      const { data: broadcast, error } = await supabase
        .from("broadcasts")
        .select('id, title, description, youtube_url, is_live, auction_id, current_lot_id')
        .eq("is_live", true as any)
        .maybeSingle();

      if (error && error.code !== 'PGRST116') {
        throw error;
      }

      if (broadcast) {
        const typedBroadcast = broadcast as LiveBroadcast;
        setLiveBroadcast(typedBroadcast);
        
        // Debug: Log the YouTube URL
        if (typedBroadcast.youtube_url) {
          console.log('Original YouTube URL:', typedBroadcast.youtube_url);
          console.log('Converted Embed URL:', getYouTubeEmbedUrl(typedBroadcast.youtube_url));
        }
        
        // Simulate viewer count (in real implementation, this would come from analytics)
        setViewerCount(Math.floor(Math.random() * 2000) + 800);
        
        // Fetch current lot data if broadcast has one
        if (typedBroadcast.current_lot_id) {
          await fetchCurrentLot(typedBroadcast.current_lot_id);
        }
        
        // Fetch upcoming lots
        if (typedBroadcast.auction_id) {
          await fetchUpcomingLots(typedBroadcast.auction_id, typedBroadcast.current_lot_id);
        }
        
        // Fetch chat messages
        await fetchChatMessages(typedBroadcast.id);
        
        // Fetch recent bids for current lot
        if (typedBroadcast.current_lot_id) {
          await fetchRecentBids(typedBroadcast.current_lot_id);
        }
      }
    } catch (error) {
      console.error("Error fetching live broadcast:", error);
    }
  };

  // Fetch current lot details
  const fetchCurrentLot = async (lotId: string) => {
    try {
      const { data: lot, error } = await supabase
        .from("lots")
        .select('id, lot_number, starting_bid, current_bid, artworks:artwork_id(title, image_url, artists:artist_id(name))')
        .eq("id", lotId as any)
        .single();

      if (error) {
        throw error;
      }

      if (lot) {
        setCurrentLot(lot as any);
      }
    } catch (error) {
      console.error("Error fetching current lot:", error);
    }
  };

  // Fetch upcoming lots
  const fetchUpcomingLots = async (auctionId: string, currentLotId: string | null) => {
    try {
      let query = supabase
        .from("lots")
        .select('id, lot_number, starting_bid, current_bid, artworks:artwork_id(title, image_url, artists:artist_id(name))')
        .eq("auction_id", auctionId as any)
        .order("lot_number", { ascending: true })
        .limit(3);

      if (currentLotId) {
        query = query.neq("id", currentLotId as any);
      }

      const { data: lots, error } = await query;

      if (error) {
        throw error;
      }

      if (lots) {
        setUpcomingLots(lots as any);
      }
    } catch (error) {
      console.error("Error fetching upcoming lots:", error);
    }
  };

  // Fetch chat messages
  const fetchChatMessages = async (broadcastId: string) => {
    try {
      const { data: messages, error } = await supabase
        .from("broadcast_comments")
        .select('id, content, created_at, user_id, profiles:user_id(first_name, last_name, paddle_number, use_paddle_for_display)')
        .eq("broadcast_id", broadcastId as any)
        .eq("is_deleted", false as any)
        .order("created_at", { ascending: false })
        .limit(20);

      if (error) {
        throw error;
      }

      if (messages) {
        setChatMessages(messages.reverse() as any);
      }
    } catch (error) {
      console.error("Error fetching chat messages:", error);
    }
  };

  // Fetch recent bids for current lot
  const fetchRecentBids = async (lotId: string) => {
    try {
      const { data: bids, error } = await supabase
        .from("bids")
        .select('id, amount, created_at, user_id, lot_id, status, profiles:user_id(first_name, last_name, paddle_number, use_paddle_for_display)')
        .eq("lot_id", lotId as any)
        .eq("status", "approved" as any)
        .order("created_at", { ascending: false })
        .limit(5);

      if (error) {
        throw error;
      }

      if (bids) {
        setRecentBids(bids as any);
      }
    } catch (error) {
      console.error("Error fetching recent bids:", error);
    }
  };

  // Send chat message
  const sendChatMessage = async () => {
    if (!user || !newChatMessage.trim() || !liveBroadcast) return;

    try {
      const { error } = await supabase
        .from("broadcast_comments")
        .insert({
          broadcast_id: liveBroadcast.id,
          user_id: user.id,
          content: newChatMessage.trim(),
        } as any);

      if (error) {
        throw error;
      }

      setNewChatMessage("");
      // Refresh chat messages
      await fetchChatMessages(liveBroadcast.id);
    } catch (error) {
      console.error("Error sending chat message:", error);
      toast({
        title: "Error",
        description: "Failed to send message. Please try again.",
        variant: "destructive",
      });
    }
  };

  // Format bidder display name
  const formatBidderName = (bid: BidActivity): string => {
    if (!bid.profiles) return "Paddle #--";
    
    // First priority: Use paddle number if enabled
    if (bid.profiles.use_paddle_for_display && bid.profiles.paddle_number) {
      return `Paddle #${bid.profiles.paddle_number}`;
    }
    
    // Second priority: If paddle number exists but not enabled, still show it
    if (bid.profiles.paddle_number) {
      return `Paddle #${bid.profiles.paddle_number}`;
    }
    
    // Third priority: Use initials if name is available
    const firstName = bid.profiles.first_name || "";
    const lastName = bid.profiles.last_name || "";
    
    if (firstName && lastName) {
      return `${firstName.charAt(0).toUpperCase()}.${lastName.charAt(0).toUpperCase()}.`;
    } else if (firstName) {
      return `${firstName.charAt(0).toUpperCase()}.`;
    }
    
    // Last resort: Generic paddle number
    return "Paddle #--";
  };

  // Format chat user name
  const formatChatUserName = (message: ChatMessage): string => {
    if (!message.profiles) return "Anonymous";
    
    const firstName = message.profiles.first_name || "";
    const lastName = message.profiles.last_name || "";
    return `${firstName} ${lastName}`.trim() || "Anonymous";
  };

  // Convert YouTube URL to embeddable format
  const getYouTubeEmbedUrl = (url: string): string => {
    try {
      // Handle different YouTube URL formats
      let videoId = '';
      
      if (url.includes('youtube.com/watch?v=')) {
        videoId = url.split('v=')[1]?.split('&')[0];
      } else if (url.includes('youtu.be/')) {
        videoId = url.split('youtu.be/')[1]?.split('?')[0];
      } else if (url.includes('youtube.com/embed/')) {
        videoId = url.split('embed/')[1]?.split('?')[0];
      } else if (url.includes('youtube.com/live/')) {
        videoId = url.split('live/')[1]?.split('?')[0];
      }
      
      if (videoId) {
        return `https://www.youtube.com/embed/${videoId}?autoplay=1&mute=1&enablejsapi=1&origin=${window.location.origin}`;
      }
      
      return url;
    } catch (error) {
      console.error('Error parsing YouTube URL:', error);
      return url;
    }
  };

  useEffect(() => {
    async function fetchData() {
      try {
        // Check for live auctions first
        const { data: liveAuctionData, error: liveError } = await supabase
          .from("auctions")
          .select("id, title, stream_url, is_live")
          .eq("is_live", true as any)
          .not("stream_url", "is", null)
          .maybeSingle();

        if (!liveError && liveAuctionData) {
          setLiveAuction(liveAuctionData as LiveAuction);
        }

        // Get upcoming auctions with direct query instead of RPC
        const now = new Date().toISOString();
        const { data: upcomingAuctions, error: auctionError } = await supabase
          .from("auctions")
          .select("*")
          .eq("status", "upcoming" as any)
          .gt("start_date", now)
          .order("start_date", { ascending: true })
          .limit(5);

        if (auctionError) throw auctionError;

        console.log("Upcoming auctions:", upcomingAuctions); // Debug log
        setFeaturedAuctions(upcomingAuctions || []);

        // Fetch featured blog posts
        const { data: blogPosts, error: blogError } = await supabase
          .from("blog_posts")
          .select('id, title, slug, excerpt, featured_image_url, published_at, reading_time_minutes, view_count, category:blog_categories(name, color), author:profiles(first_name, last_name)')
          .eq("status", "published" as any)
          .eq("is_featured", true as any)
          .order("published_at", { ascending: false })
          .limit(3);

        if (!blogError && blogPosts) {
          setFeaturedBlogPosts(blogPosts);
        }

        // Fetch live broadcast data
        await fetchLiveBroadcast();
      } catch (error) {
        console.error("Error fetching data:", error);
        toast({
          title: "Error",
          description: "Failed to fetch data. Please try again later.",
          variant: "destructive",
        });
      } finally {
        setLoading(false);
      }
    }

    fetchData();

    // Set up real-time subscription for live auction changes
    const auctionChannel = supabase
      .channel('public:auctions')
      .on(
        'postgres_changes',
        { event: 'UPDATE', schema: 'public', table: 'auctions' },
        (payload) => {
          // Check if this update affects live status
          const auction = payload.new as any;
          if (auction.is_live && auction.stream_url) {
            setLiveAuction({
              id: auction.id,
              title: auction.title,
              stream_url: auction.stream_url,
              is_live: auction.is_live
            });
          } else if (!auction.is_live && liveAuction && liveAuction.id === auction.id) {
            setLiveAuction(null);
          }
        }
      )
      .subscribe();

    // Set up real-time subscription for broadcast changes
    const broadcastChannel = supabase
      .channel('public:broadcasts')
      .on(
        'postgres_changes',
        { event: '*', schema: 'public', table: 'broadcasts' },
        (payload) => {
          if (payload.eventType === 'UPDATE' && payload.new) {
            const broadcast = payload.new as any;
            if (broadcast.is_live) {
              fetchLiveBroadcast();
            } else if (liveBroadcast && liveBroadcast.id === broadcast.id) {
              setLiveBroadcast(null);
              setCurrentLot(null);
              setUpcomingLots([]);
              setChatMessages([]);
              setRecentBids([]);
            }
          }
        }
      )
      .subscribe();

    // Set up real-time subscription for chat messages
    const chatChannel = supabase
      .channel('public:broadcast_comments')
      .on(
        'postgres_changes',
        { event: 'INSERT', schema: 'public', table: 'broadcast_comments' },
        (payload) => {
          if (liveBroadcast && payload.new) {
            const newMessage = payload.new as any;
            if (newMessage.broadcast_id === liveBroadcast.id) {
              fetchChatMessages(liveBroadcast.id);
            }
          }
        }
      )
      .subscribe();

    // Set up real-time subscription for new bids
    const bidChannel = supabase
      .channel('public:bids')
      .on(
        'postgres_changes',
        { event: '*', schema: 'public', table: 'bids' },
        (payload) => {
          if (currentLot && payload.new) {
            const bid = payload.new as any;
            if (bid.lot_id === currentLot.id) {
              fetchRecentBids(currentLot.id);
              fetchCurrentLot(currentLot.id); // Refresh current bid amount
            }
          }
        }
      )
      .subscribe();

    // Cleanup subscriptions
    return () => {
      supabase.removeChannel(auctionChannel);
      supabase.removeChannel(broadcastChannel);
      supabase.removeChannel(chatChannel);
      supabase.removeChannel(bidChannel);
    };
  }, []);

  const handleMapSelection = (mapType: 'apple' | 'google') => {
    const address = "700 Indian River Dr, Fort Pierce, FL 34949";
    const encodedAddress = encodeURIComponent(address);
    
    if (mapType === 'apple') {
      window.open(`maps://maps.apple.com/?q=${encodedAddress}`, '_blank');
    } else {
      window.open(`https://www.google.com/maps?q=${encodedAddress}`, '_blank');
    }
  };

  // Updated hero images with your provided artwork
  const heroImages = [
    "/lovable-uploads/25daf720-6e00-4ba7-9642-84ee91f0e896.png",
    "/lovable-uploads/16aa0f6f-b130-43e7-b02c-dcdfd7bd0c6f.png", 
    "/lovable-uploads/063d238d-885b-42fc-869c-cf0cf4bc890b.png",
    "/lovable-uploads/e5c83769-43dc-4636-8314-a6708fdda351.png",
    "/lovable-uploads/81c7a3ce-6fc6-47d6-8e17-4d7f6c14c7a1.png"
  ];

  return (
    <Layout>
      {/* Welcome Auction Banner */}
      <WelcomeAuctionBanner />
      
      {/* Professional Hero Section */}
      <section className="relative py-16 md:py-20 lg:py-24 xl:py-28 overflow-hidden bg-gradient-to-br from-emerald-900 via-teal-800 to-blue-900">
        {/* Sophisticated Background Effects */}
        <div className="absolute inset-0">
          <div className="absolute top-0 left-1/4 w-96 h-96 bg-emerald-500/5 rounded-full blur-3xl" />
          <div className="absolute bottom-0 right-1/4 w-96 h-96 bg-blue-500/5 rounded-full blur-3xl" />
          <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] bg-teal-500/3 rounded-full blur-3xl" />
        </div>

        {/* Subtle grid overlay */}
        <div 
          className="absolute inset-0 opacity-[0.02]"
          style={{
            backgroundImage: `url("data:image/svg+xml,%3Csvg width='32' height='32' viewBox='0 0 32 32' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='%23ffffff' fill-opacity='0.05'%3E%3Cpath d='M0 0h1v1H0zM16 16h1v1h-1z'/%3E%3C/g%3E%3C/svg%3E")`,
            backgroundSize: '32px 32px'
          }}
        />

        <div className="container mx-auto px-4 relative z-10">
          <div className="flex flex-col lg:flex-row items-center justify-between gap-8 lg:gap-16">
            {/* Left Content - Professional Typography */}
            <div className="w-full lg:w-1/2 text-center lg:text-left space-y-6 lg:space-y-8 animate-fade-in">
              <div className="inline-block rounded-full px-6 py-3 mb-6 bg-white/5 backdrop-blur-xl text-emerald-300/90 border border-white/10 shadow-xl">
                <span className="font-light text-sm lg:text-base tracking-wider uppercase">Discover Florida's Artistic Heritage</span>
              </div>
              
              <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl xl:text-7xl font-light mb-8 text-white tracking-tight leading-[1.1]">
                <span className="block animate-slide-in-left font-extralight">Florida</span>
                <span className="block animate-slide-in-left animation-delay-500 font-normal bg-gradient-to-r from-emerald-400 via-teal-300 to-blue-400 bg-clip-text text-transparent">Highwaymen</span>
                <span className="block animate-slide-in-left animation-delay-1000 font-light text-white/90">Art Vault</span>
              </h1>
              
              <p className="text-base sm:text-lg md:text-xl lg:text-2xl mb-12 text-white/80 max-w-2xl mx-auto lg:mx-0 leading-relaxed animate-slide-in-left animation-delay-1500 font-light">
                Curating and preserving the legacy of Florida's iconic landscape artists through
                <span className="text-emerald-300 font-medium"> exclusive auctions</span> and exhibitions
              </p>
              
              <div className="flex flex-col sm:flex-row gap-6 animate-slide-in-left animation-delay-2000 justify-center lg:justify-start">
                <Link to="/auctions">
                  <Button size="lg" className="w-full sm:w-auto bg-gradient-to-r from-emerald-600 to-teal-600 hover:from-emerald-500 hover:to-teal-500 text-white px-10 py-6 text-lg font-medium rounded-xl shadow-2xl hover:shadow-emerald-500/25 transition-all transform hover:scale-[1.02] hover:-translate-y-1 border border-emerald-400/20 backdrop-blur-sm">
                    Explore Auctions <ArrowRight className="ml-3 h-5 w-5" />
                  </Button>
                </Link>
                <Link to="/consign">
                  <Button size="lg" variant="ghost" className="w-full sm:w-auto px-10 py-6 text-lg font-light border border-white/20 text-white/90 hover:text-white hover:bg-white/10 transition-all rounded-xl shadow-lg hover:shadow-xl transform hover:scale-[1.02] bg-white/5 backdrop-blur-xl">
                    Consign Artwork
                  </Button>
                </Link>
              </div>
            </div>
            
            {/* Right Side - Enhanced Image Gallery with Glass Morphism */}
            <div className="w-full lg:w-1/2 relative mt-8 lg:mt-0">
              {/* Mobile/Tablet Layout */}
              <div className="block lg:hidden space-y-4 animate-fade-in animation-delay-500">
                <div className="group">
                  <div className="relative aspect-[4/3] rounded-2xl overflow-hidden shadow-2xl border border-white/20 transform hover:scale-105 transition-all duration-700 bg-white/5 backdrop-blur-sm">
                    <img 
                      src={heroImages[0]}
                      alt="Florida Highwaymen Royal Poinciana Tree" 
                      className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>
                    <div className="absolute bottom-4 left-4 right-4 text-white opacity-0 group-hover:opacity-100 transition-all duration-500 transform translate-y-4 group-hover:translate-y-0">
                      <p className="font-medium text-lg">Featured Artwork</p>
                      <p className="text-sm opacity-80 font-light">Royal Poinciana by M.A. Carroll</p>
                    </div>
                  </div>
                </div>
                
                <div className="grid grid-cols-2 gap-4">
                  <div className="group">
                    <div className="relative aspect-square rounded-xl overflow-hidden shadow-xl border border-white/20 transform hover:scale-105 transition-all duration-500 bg-white/5 backdrop-blur-sm">
                      <img 
                        src={heroImages[1]}
                        alt="Florida Coastal Scene" 
                        className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
                      />
                    </div>
                  </div>
                  <div className="group">
                    <div className="relative aspect-square rounded-xl overflow-hidden shadow-xl border border-white/20 transform hover:scale-105 transition-all duration-500 bg-white/5 backdrop-blur-sm">
                      <img 
                        src={heroImages[2]}
                        alt="Florida Everglades" 
                        className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
                      />
                    </div>
                  </div>
                </div>
              </div>

              {/* Desktop Layout - Professional Grid with Enhanced Animations */}
              <div className="hidden lg:grid grid-cols-12 grid-rows-8 gap-4 h-[500px] xl:h-[600px] animate-fade-in animation-delay-500 relative">
                {/* Subtle parallax background layers for depth */}
                <div className="absolute inset-0 animate-parallax-slow opacity-10 pointer-events-none">
                  <div className="absolute top-10 left-10 w-32 h-32 bg-gradient-to-br from-blue-400/30 to-transparent rounded-full blur-xl"></div>
                  <div className="absolute bottom-20 right-20 w-48 h-48 bg-gradient-to-br from-emerald-400/20 to-transparent rounded-full blur-2xl"></div>
                </div>
                <div className="absolute inset-0 animate-parallax-medium opacity-5 pointer-events-none">
                  <div className="absolute top-1/3 right-1/4 w-24 h-24 bg-gradient-to-br from-orange-400/40 to-transparent rounded-full blur-lg"></div>
                  <div className="absolute bottom-1/3 left-1/4 w-36 h-36 bg-gradient-to-br from-purple-400/30 to-transparent rounded-full blur-xl"></div>
                </div>
                {/* Large featured image - Main focal point with gentle animation */}
                <div className="col-span-8 row-span-5 group">
                  <div className="animate-float-gentle h-full">
                    <div className="relative h-full rounded-2xl overflow-hidden shadow-2xl border border-white/20 bg-white/5 backdrop-blur-sm hover:shadow-[0_25px_60px_rgba(8,_112,_184,_0.4)] transition-all duration-700 hover:border-white/30">
                      <img 
                        src={heroImages[0]}
                        alt="Florida Highwaymen Royal Poinciana Tree" 
                        className="w-full h-full object-cover transition-all duration-700 group-hover:scale-105 group-hover:brightness-110"
                      />
                      <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>
                      <div className="absolute bottom-6 left-6 right-6 text-white opacity-0 group-hover:opacity-100 transition-all duration-500 transform translate-y-4 group-hover:translate-y-0">
                        <h3 className="text-xl font-bold mb-2">Royal Poinciana Tree</h3>
                        <p className="text-sm text-white/90">A magnificent example of Florida's natural beauty captured by the Highwaymen artists</p>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Top right image - Subtle drift animation */}
                <div className="col-span-4 row-span-3 group">
                  <div className="animate-float-drift animation-delay-1000 h-full">
                    <div className="relative h-full rounded-xl overflow-hidden shadow-xl border border-white/20 bg-white/5 backdrop-blur-sm hover:shadow-2xl hover:shadow-blue-500/20 transition-all duration-600 hover:border-blue-300/30">
                      <img 
                        src={heroImages[1]}
                        alt="Florida Coastal Scene" 
                        className="w-full h-full object-cover transition-all duration-600 group-hover:scale-108 group-hover:saturate-110"
                      />
                    </div>
                  </div>
                </div>

                {/* Bottom left image - Wave-like animation */}
                <div className="col-span-5 row-span-3 group">
                  <div className="animate-float-wave animation-delay-2000 h-full">
                    <div className="relative h-full rounded-xl overflow-hidden shadow-xl border border-white/20 bg-white/5 backdrop-blur-sm hover:shadow-2xl hover:shadow-emerald-500/20 transition-all duration-700 hover:border-emerald-300/30">
                      <img 
                        src={heroImages[2]}
                        alt="Florida Everglades" 
                        className="w-full h-full object-cover transition-all duration-700 group-hover:scale-106 group-hover:hue-rotate-3"
                      />
                    </div>
                  </div>
                </div>

                {/* Bottom center image - Subtle float with parallax */}
                <div className="col-span-3 row-span-2 group">
                  <div className="animate-float-subtle animation-delay-3000 h-full">
                    <div className="relative h-full rounded-lg overflow-hidden shadow-lg border border-white/20 bg-white/5 backdrop-blur-sm hover:shadow-xl hover:shadow-orange-500/20 transition-all duration-500 hover:border-orange-300/30">
                      <img 
                        src={heroImages[3]}
                        alt="Florida Mangroves" 
                        className="w-full h-full object-cover transition-all duration-500 group-hover:scale-107 group-hover:contrast-110"
                      />
                    </div>
                  </div>
                </div>

                {/* Bottom right image - Enhanced float with depth */}
                <div className="col-span-4 row-span-2 group">
                  <div className="animate-float animation-delay-4000 h-full">
                    <div className="relative h-full rounded-lg overflow-hidden shadow-lg border border-white/20 bg-white/5 backdrop-blur-sm hover:shadow-xl hover:shadow-purple-500/20 transition-all duration-600 hover:border-purple-300/30">
                      <img 
                        src={heroImages[4]}
                        alt="Florida Lighthouse Storm" 
                        className="w-full h-full object-cover transition-all duration-600 group-hover:scale-105 group-hover:brightness-105"
                      />
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
        
        {/* Professional Flowing Waves with Animation */}
        <div className="absolute bottom-0 left-0 right-0 overflow-hidden">
          <svg viewBox="0 0 1200 120" preserveAspectRatio="none" className="relative block w-full h-16">
            {/* First wave layer - slow flowing motion */}
            <path 
              d="M0,0V46.29c47.79,22.2,103.59,32.17,158,28,70.36-5.37,136.33-33.31,206.8-37.5C438.64,32.43,512.34,53.67,583,72.05c69.27,18,138.3,24.88,209.4,13.08,36.15-6,69.85-17.84,104.45-29.34C989.49,25,1113-14.29,1200,52.47V0Z" 
              opacity=".25" 
              fill="rgba(16, 185, 129, 0.1)"
              className="animate-wave-flow-slow"
            />
            {/* Second wave layer - medium flowing motion */}
            <path 
              d="M0,0V15.81C13,36.92,27.64,56.86,47.69,72.05,99.41,111.27,165,111,224.58,91.58c31.15-10.15,60.09-26.07,89.67-39.8,40.92-19,84.73-46,130.83-49.67,36.26-2.85,70.9,9.42,98.6,31.56,31.77,25.39,62.32,62,103.63,73,40.44,10.79,81.35-6.69,119.13-24.28s75.16-39,116.92-43.05c59.73-5.85,113.28,22.88,168.9,38.84,30.2,8.66,59,6.17,87.09-7.5,22.43-10.89,48-26.93,60.65-49.24V0Z" 
              opacity=".5" 
              fill="rgba(20, 184, 166, 0.1)"
              className="animate-wave-flow-alt"
            />
            {/* Third wave layer - faster flowing motion */}
            <path 
              d="M0,0V5.63C149.93,59,314.09,71.32,475.83,42.57c43-7.64,84.23-20.12,127.61-26.46,59-8.63,112.48,12.24,165.56,35.4C827.93,77.22,886,95.24,951.2,90c86.53-7,172.46-45.71,248.8-84.81V0Z" 
              fill="rgba(6, 182, 212, 0.1)"
              className="animate-wave-flow"
            />
          </svg>
        </div>
      </section>

      {/* Upcoming Auction Lots Section */}
      <AdvancedUpcomingAuctions />

      {/* Services Section - Consignments, Appraisals, Contact, FAQs */}
      <ServicesSection />

      {/* Auction Lots Carousel - Wave-like sliding animation */}
      <AuctionLotsCarousel />

      {/* Professional Registration Benefits Section */}
      <section className="relative py-20 md:py-28 overflow-hidden bg-gradient-to-br from-slate-900 via-slate-950 to-slate-900">
        {/* Sophisticated Background Effects */}
        <div className="absolute inset-0">
          <div className="absolute top-1/4 left-0 w-96 h-96 bg-emerald-500/3 rounded-full blur-3xl" />
          <div className="absolute bottom-1/4 right-0 w-96 h-96 bg-blue-500/3 rounded-full blur-3xl" />
        </div>

        {/* Professional pattern overlay */}
        <div 
          className="absolute inset-0 opacity-[0.015]"
          style={{
            backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='1'%3E%3Ccircle cx='30' cy='30' r='2'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`,
            backgroundSize: '60px 60px'
          }}>
        </div>

        <div className="container mx-auto px-4 relative z-10">
          <div className="max-w-6xl mx-auto">
            <div className="text-center mb-16">
              <div className="inline-flex items-center justify-center px-8 py-3 mb-8 bg-white/5 backdrop-blur-xl rounded-full border border-white/10 shadow-lg">
                <Award className="h-5 w-5 mr-3 text-emerald-400" />
                <span className="text-white font-light tracking-wide">Collector Privileges</span>
              </div>
              <h2 className="text-5xl md:text-6xl lg:text-7xl font-extralight mb-8 text-white tracking-tight leading-tight">
                <span className="block">Exclusive Member</span>
                <span className="block bg-gradient-to-r from-emerald-400 via-teal-400 to-blue-400 bg-clip-text text-transparent font-light">Benefits</span>
              </h2>
              <p className="text-2xl text-white/70 max-w-5xl mx-auto leading-relaxed font-light">
                Join Florida's most distinguished community of <span className="text-emerald-300 font-normal">Highwaymen art collectors</span> and unlock 
                unprecedented access to authentic masterpieces with substantial savings
              </p>
              <div className="w-24 h-px bg-gradient-to-r from-transparent via-emerald-400 to-transparent mx-auto my-8" />
            </div>

            {/* Premium Benefit Highlight */}
            <div className="relative mb-16">
              <div className="bg-gradient-to-r from-emerald-600/90 via-teal-600/90 to-blue-600/90 backdrop-blur-xl rounded-3xl p-12 border border-white/20 shadow-2xl text-center text-white overflow-hidden">
                <div className="absolute inset-0 bg-white/5 backdrop-blur-sm" />
                <div className="relative z-10">
                  <div className="flex items-center justify-center mb-6">
                    <DollarSign className="w-12 h-12 mr-3" />
                    <span className="text-6xl font-extralight">25%</span>
                  </div>
                  <h3 className="text-3xl font-light mb-4 tracking-wide">COLLECTOR'S PREMIUM DISCOUNT</h3>
                  <p className="text-xl text-white/90 font-light leading-relaxed">Distinguished members enjoy a 25% reduction on all buyer's premiums - maximizing your investment in authentic Highwaymen masterpieces</p>
                </div>
              </div>
            </div>

            {/* Benefits Grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {/* Priority Viewing */}
              <motion.div 
                className="relative group"
                whileHover={{ scale: 1.02, y: -5 }}
                transition={{ duration: 0.3 }}
              >
                <div className="bg-white/5 backdrop-blur-xl rounded-2xl p-8 border border-white/10 hover:border-white/20 shadow-lg hover:shadow-2xl transition-all duration-300 h-full">
                  <div className="flex items-center mb-6">
                    <div className="w-14 h-14 bg-white/10 backdrop-blur-sm rounded-xl flex items-center justify-center mr-4">
                      <Clock className="w-7 h-7 text-blue-300" />
                  </div>
                    <h3 className="text-xl font-light text-white tracking-wide">Priority Viewing</h3>
                </div>
                  <p className="text-white/70 leading-relaxed font-light">Exclusive first access to newly discovered Highwaymen works and private estate collections before public release</p>
                </div>
              </motion.div>

              {/* Curated Selections */}
              <motion.div 
                className="relative group"
                whileHover={{ scale: 1.02, y: -5 }}
                transition={{ duration: 0.3 }}
              >
                <div className="bg-white/5 backdrop-blur-xl rounded-2xl p-8 border border-white/10 hover:border-white/20 shadow-lg hover:shadow-2xl transition-all duration-300 h-full">
                  <div className="flex items-center mb-6">
                    <div className="w-14 h-14 bg-white/10 backdrop-blur-sm rounded-xl flex items-center justify-center mr-4">
                      <Target className="w-7 h-7 text-emerald-300" />
                  </div>
                    <h3 className="text-xl font-light text-white tracking-wide">Curated Selections</h3>
                </div>
                  <p className="text-white/70 leading-relaxed font-light">Personalized artwork recommendations based on your collection interests and artist preferences from our expert curators</p>
                </div>
              </motion.div>

              {/* Collection Tracker */}
              <motion.div 
                className="relative group"
                whileHover={{ scale: 1.02, y: -5 }}
                transition={{ duration: 0.3 }}
              >
                <div className="bg-white/5 backdrop-blur-xl rounded-2xl p-8 border border-white/10 hover:border-white/20 shadow-lg hover:shadow-2xl transition-all duration-300 h-full">
                  <div className="flex items-center mb-6">
                    <div className="w-14 h-14 bg-white/10 backdrop-blur-sm rounded-xl flex items-center justify-center mr-4">
                      <Heart className="w-7 h-7 text-teal-300" />
                    </div>
                    <h3 className="text-xl font-light text-white tracking-wide">Collection Tracker</h3>
                  </div>
                  <p className="text-white/70 leading-relaxed font-light">Build your personal watchlist of coveted pieces with real-time alerts when similar works become available</p>
                </div>
              </motion.div>

              {/* Expert Authentication */}
              <motion.div 
                className="relative group"
                whileHover={{ scale: 1.02, y: -5 }}
                transition={{ duration: 0.3 }}
              >
                <div className="bg-white/5 backdrop-blur-xl rounded-2xl p-8 border border-white/10 hover:border-white/20 shadow-lg hover:shadow-2xl transition-all duration-300 h-full">
                  <div className="flex items-center mb-6">
                    <div className="w-14 h-14 bg-white/10 backdrop-blur-sm rounded-xl flex items-center justify-center mr-4">
                      <Shield className="w-7 h-7 text-emerald-300" />
                    </div>
                    <h3 className="text-xl font-light text-white tracking-wide">Authenticity Guarantee</h3>
                  </div>
                  <p className="text-white/70 leading-relaxed font-light">Each artwork certified by recognized Highwaymen scholars with comprehensive provenance documentation and lifetime authenticity assurance</p>
                </div>
              </motion.div>

              {/* Secure Bidding */}
              <motion.div 
                className="relative group"
                whileHover={{ scale: 1.02, y: -5 }}
                transition={{ duration: 0.3 }}
              >
                <div className="bg-white/5 backdrop-blur-xl rounded-2xl p-8 border border-white/10 hover:border-white/20 shadow-lg hover:shadow-2xl transition-all duration-300 h-full">
                  <div className="flex items-center mb-6">
                    <div className="w-14 h-14 bg-white/10 backdrop-blur-sm rounded-xl flex items-center justify-center mr-4">
                      <Lock className="w-7 h-7 text-blue-300" />
                    </div>
                    <h3 className="text-xl font-light text-white tracking-wide">Secure Transactions</h3>
                  </div>
                  <p className="text-white/70 leading-relaxed font-light">Museum-grade security protocols with encrypted bidding systems and trusted payment processing for valuable acquisitions</p>
                </div>
              </motion.div>

              {/* Full Service */}
              <motion.div 
                className="relative group"
                whileHover={{ scale: 1.02, y: -5 }}
                transition={{ duration: 0.3 }}
              >
                <div className="bg-white/5 backdrop-blur-xl rounded-2xl p-8 border border-white/10 hover:border-white/20 shadow-lg hover:shadow-2xl transition-all duration-300 h-full">
                  <div className="flex items-center mb-6">
                    <div className="w-14 h-14 bg-white/10 backdrop-blur-sm rounded-xl flex items-center justify-center mr-4">
                      <Truck className="w-7 h-7 text-teal-300" />
                    </div>
                    <h3 className="text-xl font-light text-white tracking-wide">Concierge Service</h3>
                  </div>
                  <p className="text-white/70 leading-relaxed font-light">Complete acquisition support including professional conservation framing, secure transportation, and installation coordination</p>
                </div>
              </motion.div>
            </div>

            {/* Professional Call to Action */}
            <div className="text-center mt-16">
              <Link to="/register">
                <Button className="bg-gradient-to-r from-emerald-600 via-teal-600 to-blue-600 hover:from-emerald-500 hover:via-teal-500 hover:to-blue-500 text-white font-light px-12 py-4 rounded-xl text-lg backdrop-blur-sm border border-white/20 shadow-lg hover:shadow-2xl transition-all duration-300 hover:scale-105">
                  <UserPlus className="mr-3 h-6 w-6" />
                  Begin Your Collection Journey
                  <ArrowRight className="ml-3 h-6 w-6" />
                </Button>
              </Link>
              <p className="text-sm text-white/50 mt-4 font-light tracking-wide">
                Complimentary membership • Immediate privileges • Transparent pricing
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Live Stream Section - Only visible when there's an active stream */}
      {liveAuction && (
        <section className="py-4 bg-slate-900 border-y border-slate-800">
          <div className="container mx-auto px-4">
            <div className="max-w-4xl mx-auto">
              <LiveAuctionStream 
                streamUrl={liveAuction.stream_url} 
                title={liveAuction.title} 
              />
            </div>
          </div>
        </section>
      )}

      {/* Live Auction Experience - Only show if there's a live broadcast */}
      {liveBroadcast && (
        <section className="relative py-16 md:py-24 overflow-hidden bg-gradient-to-br from-emerald-900 via-teal-800 to-blue-900">
          {/* Professional Background Effects */}
          <div className="absolute inset-0">
            <div className="absolute top-0 left-1/3 w-96 h-96 bg-red-500/3 rounded-full blur-3xl" />
            <div className="absolute bottom-0 right-1/3 w-96 h-96 bg-emerald-500/3 rounded-full blur-3xl" />
          </div>

          <div className="container mx-auto px-4 relative z-10">
            {/* Professional Header */}
            <div className="text-center mb-16">
              <div className="inline-flex items-center justify-center px-8 py-3 mb-8 bg-white/5 backdrop-blur-xl rounded-full border border-white/10 shadow-xl">
                <div className="w-3 h-3 bg-red-500 rounded-full animate-pulse mr-3"></div>
                <span className="text-white/90 font-light tracking-wider uppercase text-sm">Live Auction Experience</span>
              </div>
              
              <h2 className="text-4xl md:text-5xl lg:text-6xl font-light mb-6 text-white tracking-tight">
                <span className="bg-gradient-to-r from-red-400 via-rose-300 to-orange-400 bg-clip-text text-transparent">Live</span> Bidding Now
              </h2>
              
              <p className="text-xl text-white/70 max-w-3xl mx-auto leading-relaxed font-light">
                Join the excitement of real-time bidding on authentic Florida Highwaymen artwork
              </p>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 mb-12">
              {/* Main Live Stream Area */}
              <div className="lg:col-span-8">
                <div className="bg-white/5 backdrop-blur-xl rounded-3xl shadow-2xl border border-white/10 overflow-hidden">
                  {/* Stream Header */}
                  <div className="bg-gradient-to-r from-red-600/80 to-rose-600/80 backdrop-blur-sm p-6 text-white border-b border-white/10">
                    <div className="flex items-center justify-between">
                      <div>
                        {currentLot ? (
                          <>
                            <h3 className="text-2xl font-medium mb-2">Current Lot: #{currentLot.lot_number}</h3>
                            <p className="text-white/90 font-light">
                              "{currentLot.artwork?.title || 'Untitled'}" 
                              {currentLot.artwork?.artist?.name && ` by ${currentLot.artwork.artist.name}`}
                            </p>
                          </>
                        ) : (
                          <>
                            <h3 className="text-2xl font-medium mb-2">Live Auction Stream</h3>
                            <p className="text-white/90 font-light">Awaiting next lot...</p>
                          </>
                        )}
                      </div>
                      <div className="text-right">
                        {currentLot && (
                          <>
                            <div className="text-3xl font-light text-emerald-300">
                              ${(currentLot.current_bid || currentLot.starting_bid).toLocaleString()}
                            </div>
                            <div className="text-sm text-white/80 font-light">
                              {currentLot.current_bid ? 'Current Bid' : 'Starting Bid'}
                            </div>
                          </>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Stream Content with Professional Layout */}
                  <div className="p-6">
                    <div className="aspect-video bg-slate-800/50 backdrop-blur-sm rounded-2xl border border-white/10 mb-6 flex items-center justify-center">
                      {streamError ? (
                        <div className="text-center">
                          <div className="h-16 w-16 text-white/30 mx-auto mb-4 flex items-center justify-center">📹</div>
                          <p className="text-white/60 font-light">Stream temporarily unavailable</p>
                        </div>
                      ) : liveBroadcast?.youtube_url ? (
                        <div className="w-full h-full rounded-2xl overflow-hidden">
                          <iframe
                            src={getYouTubeEmbedUrl(liveBroadcast.youtube_url)}
                            title={liveBroadcast.title}
                            className="w-full h-full border-0"
                            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                            allowFullScreen
                            referrerPolicy="strict-origin-when-cross-origin"
                          />
                        </div>
                      ) : (
                        <div className="text-center">
                          <div className="h-16 w-16 text-white/30 mx-auto mb-4 flex items-center justify-center">📹</div>
                          <p className="text-white/60 font-light">Preparing live stream...</p>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>

              {/* Professional Sidebar */}
              <div className="lg:col-span-4 space-y-6">
                {/* Live Chat */}
                <div className="bg-white/5 backdrop-blur-xl rounded-2xl border border-white/10 shadow-xl">
                  <div className="p-4 border-b border-white/10">
                    <h4 className="font-medium text-white flex items-center">
                      <MessageCircle className="h-5 w-5 mr-2 text-blue-400" />
                      Live Chat
                    </h4>
                  </div>
                  <ScrollArea className="h-64 p-4">
                    {chatMessages.length > 0 ? (
                      <div className="space-y-3">
                        {chatMessages.slice(-10).map((message, index) => (
                          <div key={index} className="text-sm">
                            <span className="font-medium text-emerald-300">User:</span>
                            <span className="text-white/80 ml-2 font-light">{message.content}</span>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <div className="text-center text-white/50 font-light">No messages yet</div>
                    )}
                  </ScrollArea>
                </div>

                {/* Recent Bids */}
                <div className="bg-white/5 backdrop-blur-xl rounded-2xl border border-white/10 shadow-xl">
                  <div className="p-4 border-b border-white/10">
                    <h4 className="font-medium text-white flex items-center">
                      <TrendingUp className="h-5 w-5 mr-2 text-emerald-400" />
                      Recent Bids
                    </h4>
                  </div>
                  <div className="p-4 space-y-3">
                    {recentBids.length > 0 ? (
                      recentBids.slice(0, 5).map((bid, index) => (
                        <div key={index} className="flex justify-between items-center text-sm">
                          <span className="text-white/70 font-light">Recent Bid</span>
                          <span className="text-emerald-300 font-medium">${bid.amount.toLocaleString()}</span>
                        </div>
                      ))
                    ) : (
                      <div className="text-center text-white/50 font-light">No bids yet</div>
                    )}
                  </div>
                </div>
              </div>
            </div>

            {/* Professional CTA */}
            <div className="text-center">
              <div className="flex gap-4 justify-center">
                {user ? (
                  <Link to="/broadcast">
                    <Button size="lg" className="bg-gradient-to-r from-red-600 to-rose-600 hover:from-red-500 hover:to-rose-500 text-white font-medium px-8 py-3 shadow-2xl hover:shadow-red-500/25 transition-all transform hover:scale-[1.02] rounded-xl">
                      🔴 Join Live Auction
                    </Button>
                  </Link>
                ) : (
                  <Link to="/login">
                    <Button size="lg" className="bg-gradient-to-r from-emerald-600 to-teal-600 hover:from-emerald-500 hover:to-teal-500 text-white font-medium px-8 py-3 shadow-2xl hover:shadow-emerald-500/25 transition-all transform hover:scale-[1.02] rounded-xl">
                      🚀 Register to Bid
                    </Button>
                  </Link>
                )}
              </div>
            </div>
          </div>
        </section>
      )}

      {/* Featured Auctions with 1x2 grid layout */}
      <section className="py-20 bg-gradient-to-br from-slate-950 via-slate-900 to-slate-950 relative overflow-hidden">
        {/* Professional lighting effects */}
        <div className="absolute inset-0">
          <div className="absolute top-0 left-1/4 w-96 h-96 bg-emerald-500/5 rounded-full blur-3xl" />
          <div className="absolute bottom-0 right-1/4 w-96 h-96 bg-blue-500/5 rounded-full blur-3xl" />
        </div>

        <div className="container mx-auto px-4 relative">
          <div className="flex justify-between items-center mb-12">
            <div>
              <Badge className="mb-4 bg-emerald-500/10 text-emerald-300 border border-emerald-400/20 backdrop-blur-xl font-light tracking-wide">
                Don't Miss Out
              </Badge>
              <h2 className="text-3xl font-light text-white tracking-wide">Upcoming Auctions</h2>
            </div>
            <Link to="/auctions" className="flex items-center text-blue-300 hover:text-emerald-300 font-light tracking-wide transition-colors duration-300">
              View all auctions <ChevronRight className="h-4 w-4 ml-1" />
            </Link>
          </div>

          {loading ? (
            <div className="grid gap-8 md:grid-cols-2 lg:grid-cols-2">
              {[1, 2].map((i) => (
                <Card key={i} className="bg-white/5 backdrop-blur-xl border border-white/10 shadow-xl animate-pulse">
                  <CardHeader className="h-32"></CardHeader>
                  <CardContent className="h-64"></CardContent>
                  <CardFooter className="h-10"></CardFooter>
                </Card>
              ))}
            </div>
          ) : (
            <div className="grid gap-8 md:grid-cols-1 lg:grid-cols-2">
              {/* Left column - Featured upcoming stream */}
              <div className="h-full">
                <UpcomingStreamPreview />
              </div>

              {/* Right column - List of upcoming auctions */}
              <div className="h-full">
                {featuredAuctions.length > 0 ? (
                  <Card className="h-full bg-white/5 backdrop-blur-xl border border-white/10 shadow-xl">
                    <CardHeader>
                      <CardTitle className="text-xl text-white font-light tracking-wide">Upcoming Auctions</CardTitle>
                      <CardDescription className="text-white/70 font-light">
                        Mark your calendar for these exciting events
                      </CardDescription>
                    </CardHeader>
                    <CardContent className="pb-0">
                      <div className="space-y-4">
                        {featuredAuctions.slice(0, 4).map((auction) => (
                          <Link to={`/auctions/${auction.id}`} key={auction.id} className="block">
                            <div className="flex items-start gap-4 p-3 rounded-xl hover:bg-white/5 transition-all duration-300 group">
                              <div className="min-w-[80px] h-[60px] bg-white/10 backdrop-blur-sm rounded-lg overflow-hidden border border-white/10">
                                {auction.image_url ? (
                                  <img 
                                    src={auction.image_url} 
                                    alt={auction.title} 
                                    className="h-full w-full object-cover group-hover:scale-105 transition-transform duration-300"
                                  />
                                ) : (
                                  <div className="h-full w-full flex items-center justify-center">
                                    <Calendar className="h-6 w-6 text-blue-400" />
                                  </div>
                                )}
                              </div>
                              
                              <div className="flex-grow">
                                <h4 className="font-light text-white mb-1 line-clamp-1 tracking-wide">{auction.title}</h4>
                                <div className="flex items-center text-sm text-white/60">
                                  <Calendar className="h-3.5 w-3.5 mr-1 text-emerald-400" />
                                  {new Date(auction.start_date).toLocaleDateString('en-US', { 
                                    month: 'short', 
                                    day: 'numeric',
                                    year: 'numeric'
                                  })}
                                </div>
                              </div>
                              
                              <ChevronRight className="h-5 w-5 text-blue-400 group-hover:text-emerald-400 transition-colors duration-300" />
                            </div>
                          </Link>
                        ))}
                      </div>
                    </CardContent>
                    <CardFooter className="pt-4">
                      <Link to="/auctions" className="w-full">
                        <Button className="w-full bg-gradient-to-r from-emerald-600 to-teal-600 hover:from-emerald-500 hover:to-teal-500 text-white font-light shadow-lg hover:shadow-xl transition-all duration-300 border-0 backdrop-blur-xl tracking-wide hover:scale-[1.02] hover:-translate-y-0.5">
                          View All Auctions <ArrowRight className="ml-2 h-4 w-4" />
                        </Button>
                      </Link>
                    </CardFooter>
                  </Card>
                ) : (
                  <Card className="h-full flex flex-col justify-center items-center p-8 text-center bg-white/5 backdrop-blur-xl border border-white/10 shadow-xl">
                    <Calendar className="h-12 w-12 text-blue-400 mb-4" />
                    <h3 className="text-lg font-light mb-2 text-white tracking-wide">No Upcoming Auctions</h3>
                    <p className="text-white/70 mb-4 font-light">
                      Check back soon for new auction opportunities.
                    </p>
                  </Card>
                )}
              </div>
            </div>
          )}
        </div>
      </section>
      
      {/* NEW SECTION: Historical Timeline */}
      <LivingGallery />
      
      {/* Past Auctions Section - NEW DYNAMIC SECTION */}
      <PastAuctions />

      {/* Featured Blog Posts */}
      {featuredBlogPosts.length > 0 && (
        <section className="py-20 bg-white dark:bg-gray-950">
          <div className="container mx-auto px-4">
            <div className="flex justify-between items-center mb-12">
              <div>
                <Badge className="mb-2 bg-brown-100 text-brown-800 dark:bg-brown-900 dark:text-brown-200">
                  Latest Stories
                </Badge>
                <h2 className="text-3xl font-bold text-brown-900 dark:text-brown-100">Featured Blog Posts</h2>
                <p className="mt-2 text-gray-600 dark:text-gray-400">
                  Dive deeper into the world of Florida Highwaymen art
                </p>
              </div>
              <Link to="/blog" className="flex items-center text-brown-700 dark:text-brown-400 hover:text-brown-900 dark:hover:text-brown-300 font-medium">
                View all posts <ChevronRight className="h-4 w-4 ml-1" />
              </Link>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {featuredBlogPosts.map((post) => (
                <Link to={`/blog/${post.slug}`} key={post.id} className="block group">
                  <Card className="border border-brown-100 dark:border-brown-900 shadow-md group-hover:shadow-lg transition-all h-full flex flex-col">
                    {post.featured_image_url && (
                      <div className="relative h-48 w-full overflow-hidden rounded-t-lg">
                        <img 
                          src={post.featured_image_url} 
                          alt={post.title}
                          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                        />
                        {post.category && (
                          <Badge 
                            className="absolute top-3 left-3"
                            style={{ 
                              backgroundColor: post.category.color + "90", 
                              color: "white"
                            }}
                          >
                            {post.category.name}
                          </Badge>
                        )}
                      </div>
                    )}
                    
                    <CardContent className="p-6 flex-grow flex flex-col">
                      <h3 className="text-lg font-bold mb-2 line-clamp-2 group-hover:text-brown-700 dark:group-hover:text-brown-300 transition-colors">
                        {post.title}
                      </h3>
                      
                      <p className="text-gray-600 dark:text-gray-400 mb-4 line-clamp-3 flex-grow">
                        {post.excerpt}
                      </p>

                      <div className="flex items-center justify-between text-sm text-gray-500 dark:text-gray-400">
                        <div className="flex items-center gap-3">
                          {post.author && (
                            <span className="flex items-center gap-1">
                              <User className="h-3 w-3" />
                              {post.author.first_name} {post.author.last_name}
                            </span>
                          )}
                          
                          <span className="flex items-center gap-1">
                            <Clock className="h-3 w-3" />
                            {post.reading_time_minutes} min
                          </span>
                        </div>

                        <span className="flex items-center gap-1">
                          <Calendar className="h-3 w-3" />
                          {new Date(post.published_at).toLocaleDateString('en-US', { 
                            month: 'short', 
                            day: 'numeric'
                          })}
                        </span>
                      </div>
                    </CardContent>
                  </Card>
                </Link>
              ))}
            </div>

            <div className="text-center mt-10">
              <Link to="/blog">
                <Button variant="outline" className="border-brown-300 text-brown-700 hover:bg-brown-50 dark:border-brown-700 dark:text-brown-300 dark:hover:bg-brown-900/50">
                  Read More Stories <ArrowRight className="ml-2 h-4 w-4" />
                </Button>
              </Link>
            </div>
          </div>
        </section>
      )}

      {/* Professional Artists Showcase */}
      <section className="relative py-20 md:py-28 overflow-hidden bg-gradient-to-br from-slate-950 via-slate-900 to-slate-950">
        {/* Sophisticated Background Effects */}
        <div className="absolute inset-0">
          <div className="absolute top-0 left-1/4 w-96 h-96 bg-emerald-500/4 rounded-full blur-3xl" />
          <div className="absolute bottom-0 right-1/4 w-96 h-96 bg-blue-500/4 rounded-full blur-3xl" />
          <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] bg-teal-500/2 rounded-full blur-3xl" />
        </div>

        <div className="container mx-auto px-4 relative z-10">
          {/* Professional Header */}
          <div className="text-center mb-20">
            <div className="inline-flex items-center justify-center px-8 py-3 mb-8 bg-white/5 backdrop-blur-xl rounded-full border border-white/10 shadow-xl">
              <Palette className="h-5 w-5 mr-3 text-emerald-400" />
              <span className="text-white/90 font-light tracking-wider uppercase text-sm">Meet the Masters</span>
            </div>
            
            <h2 className="text-5xl md:text-6xl lg:text-7xl font-light mb-8 text-white tracking-tight leading-[1.1]">
              <span className="block font-extralight">Florida</span>
              <span className="block bg-gradient-to-r from-emerald-400 via-teal-300 to-green-400 bg-clip-text text-transparent font-normal">Highwaymen</span>
              <span className="block font-light text-white/90">Master Artists</span>
            </h2>
            
            <p className="text-xl text-white/70 max-w-4xl mx-auto leading-relaxed font-light mb-6">
              Discover the legendary African-American landscape artists who revolutionized Florida art in the 1950s-80s
            </p>

            {/* Professional Credit Section */}
            <div className="bg-white/5 backdrop-blur-xl rounded-2xl border border-white/10 shadow-xl p-8 max-w-3xl mx-auto">
              <div className="flex items-center justify-center mb-4">
                <Award className="h-6 w-6 text-emerald-400 mr-3" />
                <span className="text-white font-medium">Historical Recognition</span>
              </div>
              <p className="text-white/80 leading-relaxed font-light">
                The Florida Highwaymen were a group of 26 African-American landscape artists who painted 
                <span className="text-emerald-300 font-medium"> over 50,000 works</span> and sold them from their cars 
                along Florida's highways during the era of segregation.
              </p>
            </div>
          </div>

          {/* Professional Artists Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 mb-16">
            {[
              {
                name: "Alfred Hair",
                role: "Founding Artist",
                description: "The charismatic leader who taught and inspired an entire generation of Highwaymen artists.",
                accent: "from-emerald-500 to-teal-600"
              },
              {
                name: "Harold Newton",
                role: "Master Painter",
                description: "Known for his dramatic seascapes and moonlit scenes that captured Florida's natural beauty.",
                accent: "from-blue-500 to-indigo-600"
              },
              {
                name: "Mary Ann Carroll",
                role: "Pioneering Artist",
                description: "One of the few female Highwaymen, renowned for her vibrant poinciana tree paintings.",
                accent: "from-teal-500 to-cyan-600"
              },
              {
                name: "Al Black",
                role: "Landscape Specialist",
                description: "Master of Florida's wetlands and cypress swamps, creating atmospheric masterpieces.",
                accent: "from-green-500 to-emerald-600"
              },
              {
                name: "R.L. Lewis",
                role: "Technical Pioneer",
                description: "Innovator in fast-drying techniques that allowed for rapid artistic production.",
                accent: "from-blue-600 to-sky-600"
              },
              {
                name: "James Gibson",
                role: "Colorist Master",
                description: "Celebrated for his bold use of color in depicting Florida's tropical landscapes.",
                accent: "from-emerald-600 to-green-600"
              }
            ].map((artist, index) => (
              <div key={index} className="group">
                <div className="bg-white/5 backdrop-blur-xl rounded-2xl border border-white/10 shadow-xl hover:shadow-2xl transition-all duration-500 hover:border-white/20 p-8 text-center">
                  {/* Artist Avatar */}
                  <div className={`mx-auto w-20 h-20 rounded-full bg-gradient-to-br ${artist.accent} flex items-center justify-center mb-6 shadow-2xl`}>
                    <User className="h-10 w-10 text-white" />
                  </div>
                  
                  {/* Artist Info */}
                  <h3 className="text-xl font-medium text-white mb-2">{artist.name}</h3>
                  <div className={`inline-block px-4 py-1 rounded-full bg-gradient-to-r ${artist.accent} bg-opacity-20 border border-white/20 mb-4`}>
                    <span className="text-white/90 text-sm font-light">{artist.role}</span>
                  </div>
                  <p className="text-white/70 font-light leading-relaxed text-sm">
                    {artist.description}
                  </p>
                </div>
              </div>
            ))}
          </div>

          {/* Professional Legacy Stats */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6 mb-16">
            {[
              { number: "26", label: "Original Artists", icon: Users },
              { number: "50,000+", label: "Artworks Created", icon: Palette },
              { number: "1950s-80s", label: "Active Period", icon: Clock },
              { number: "Museum", label: "Collections", icon: Award }
            ].map((stat, index) => (
              <div key={index} className="text-center">
                <div className="bg-white/5 backdrop-blur-xl rounded-2xl p-6 border border-white/10 shadow-xl hover:shadow-2xl transition-all duration-300 hover:border-white/20">
                  <div className="w-12 h-12 rounded-xl bg-white/10 backdrop-blur-sm flex items-center justify-center mx-auto mb-4 border border-white/20">
                    <stat.icon className="h-6 w-6 text-white/80" />
                  </div>
                  <div className="text-2xl font-light text-white mb-2">{stat.number}</div>
                  <div className="text-white/70 font-light text-sm tracking-wide">{stat.label}</div>
                </div>
              </div>
            ))}
          </div>

          {/* Professional CTA */}
          <div className="text-center">
            <div className="bg-white/5 backdrop-blur-xl rounded-3xl shadow-2xl border border-white/10 p-12 max-w-4xl mx-auto">
              <h3 className="text-3xl font-light text-white mb-6">
                Explore the Complete Legacy
              </h3>
              <p className="text-lg text-white/80 mb-8 max-w-2xl mx-auto leading-relaxed font-light">
                Learn about each artist's unique style, discover their most celebrated works, 
                and understand their impact on American art history.
              </p>
              
              <div className="flex flex-col sm:flex-row gap-6 justify-center items-center">
                <Link to="/artists">
                  <Button size="lg" className="bg-gradient-to-r from-emerald-600 to-teal-600 hover:from-emerald-500 hover:to-teal-500 text-white px-10 py-4 text-lg font-medium shadow-2xl hover:shadow-emerald-500/25 transition-all transform hover:scale-[1.02] hover:-translate-y-1 rounded-xl border border-emerald-400/20">
                    Meet All Artists <ArrowRight className="ml-3 h-5 w-5" />
                  </Button>
                </Link>
                <Link to="/artworks">
                  <Button size="lg" variant="ghost" className="text-white/80 hover:text-white border border-white/20 hover:bg-white/10 px-10 py-4 text-lg font-light transition-all rounded-xl shadow-lg hover:shadow-xl transform hover:scale-[1.02] bg-white/5 backdrop-blur-xl">
                    View Their Art
                  </Button>
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Sell With Us Section - Redesigned */}
              <section className="py-24 bg-gradient-to-br from-emerald-900 via-teal-800 to-blue-900 relative overflow-hidden">
        {/* Professional Background Effects */}
        <div className="absolute inset-0">
          <div className="absolute top-0 left-1/4 w-96 h-96 bg-emerald-500/5 rounded-full blur-3xl" />
          <div className="absolute bottom-0 right-1/4 w-96 h-96 bg-blue-500/5 rounded-full blur-3xl" />
          <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] bg-teal-500/3 rounded-full blur-3xl" />
        </div>

        {/* Subtle grid overlay */}
        <div 
          className="absolute inset-0 opacity-[0.02]"
          style={{
            backgroundImage: `url("data:image/svg+xml,%3Csvg width='32' height='32' viewBox='0 0 32 32' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='%23ffffff' fill-opacity='0.05'%3E%3Cpath d='M0 0h1v1H0zM16 16h1v1h-1z'/%3E%3C/g%3E%3C/svg%3E")`,
            backgroundSize: '32px 32px'
          }}
        />

        <div className="container mx-auto px-4 relative z-10">
          {/* Enhanced Header */}
          <div className="text-center mb-16">
            <div className="inline-flex items-center justify-center px-8 py-3 mb-8 bg-white/5 backdrop-blur-xl rounded-full border border-white/10 shadow-lg">
              <Handshake className="h-5 w-5 mr-3 text-emerald-400" />
              <span className="text-white font-light tracking-wide">Consignment Partnership</span>
            </div>
            
            <h2 className="text-5xl md:text-6xl lg:text-7xl font-extralight mb-8 text-white tracking-tight leading-tight">
              <span className="block">Sell Your</span>
              <span className="block bg-gradient-to-r from-emerald-400 via-teal-400 to-blue-400 bg-clip-text text-transparent font-light">
                Highwaymen Art
              </span>
            </h2>
            
            <p className="text-2xl text-white/70 max-w-5xl mx-auto leading-relaxed font-light">
              Partner with Florida's premier Highwaymen auction house and connect your artwork with serious collectors worldwide
            </p>
          </div>

          {/* Enhanced Process Steps */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 mb-16">
                         {/* Left Side - Hero Image/Visual */}
             <div className="relative">
              <div className="group relative h-full min-h-[500px] rounded-3xl overflow-hidden shadow-2xl border border-white/10">
                <div className="absolute inset-0 bg-gradient-to-br from-slate-900 to-slate-950"></div>
                <div className="absolute inset-0 opacity-30">
                   <img 
                     src="https://p1.liveauctioneers.com/9289/372549/208419151_1_x.jpg?quality=65&sharpen=true&version=1749153407"
                     alt="Highwaymen Artwork Consignment"
                     className="w-full h-full object-cover"
                     loading="lazy"
                     decoding="async"
                     width="1920"
                     height="1080"
                   />
                </div>
                
                {/* Content Overlay */}
                <div className="absolute inset-0 p-8 flex flex-col justify-center text-white">
                  <div className="mb-6">
                    <div className="bg-white/10 backdrop-blur-xl rounded-2xl p-4 w-16 h-16 flex items-center justify-center mb-4 border border-white/20">
                      <DollarSign className="h-8 w-8 text-emerald-400" />
                    </div>
                    <h3 className="text-3xl font-light mb-4 leading-tight tracking-wide">
                      Maximize Your <span className="font-normal">Returns</span>
                    </h3>
                    <p className="text-lg text-white/90 mb-6 leading-relaxed font-light">
                      Our expert team and extensive collector network ensure your Highwaymen artwork reaches its full market potential.
                    </p>
                  </div>
                  
                  {/* Success Stats */}
                  <div className="grid grid-cols-2 gap-4">
                    <div className="bg-white/10 backdrop-blur-xl rounded-xl p-4 text-center border border-white/10">
                      <div className="text-2xl font-light text-emerald-400 tracking-wide">95%</div>
                      <div className="text-sm text-white/70 font-light">Sale Success Rate</div>
                </div>
                    <div className="bg-white/10 backdrop-blur-xl rounded-xl p-4 text-center border border-white/10">
                      <div className="text-2xl font-light text-blue-400 tracking-wide">48hr</div>
                      <div className="text-sm text-white/70 font-light">Response Time</div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Right Side - Process Steps */}
            <div className="space-y-6">
              <h3 className="text-2xl font-light text-white mb-8 tracking-wide">
                Simple Consignment Process
              </h3>

              {/* Step 1 */}
              <div className="group relative bg-white/5 backdrop-blur-xl rounded-2xl p-6 shadow-xl border border-white/10 hover:border-white/20 hover:shadow-2xl transition-all duration-300">
                <div className="flex items-start gap-4">
                  <div className="bg-gradient-to-br from-emerald-500 to-emerald-600 p-3 rounded-xl text-white font-light text-lg min-w-[48px] h-12 flex items-center justify-center shadow-lg">
                    1
                  </div>
                  <div className="flex-grow">
                    <div className="flex items-center gap-3 mb-3">
                      <PackageOpen className="h-6 w-6 text-emerald-400" />
                      <h4 className="text-xl font-light text-white tracking-wide">Submit Your Artwork</h4>
                    </div>
                    <p className="text-white/70 leading-relaxed font-light">
                      Complete our secure online form with detailed photos and information about your Highwaymen piece. Our experts review every submission personally.
                    </p>
                  </div>
                </div>
              </div>

              {/* Step 2 */}
              <div className="group relative bg-white/5 backdrop-blur-xl rounded-2xl p-6 shadow-xl border border-white/10 hover:border-white/20 hover:shadow-2xl transition-all duration-300">
                <div className="flex items-start gap-4">
                  <div className="bg-gradient-to-br from-teal-500 to-teal-600 p-3 rounded-xl text-white font-light text-lg min-w-[48px] h-12 flex items-center justify-center shadow-lg">
                    2
                </div>
                  <div className="flex-grow">
                    <div className="flex items-center gap-3 mb-3">
                      <FileText className="h-6 w-6 text-teal-400" />
                      <h4 className="text-xl font-light text-white tracking-wide">Expert Authentication</h4>
                    </div>
                    <p className="text-white/70 leading-relaxed font-light">
                      Our certified specialists authenticate and appraise your artwork using the latest market data and historical sales records.
                    </p>
                  </div>
                </div>
          </div>

              {/* Step 3 */}
              <div className="group relative bg-white/5 backdrop-blur-xl rounded-2xl p-6 shadow-xl border border-white/10 hover:border-white/20 hover:shadow-2xl transition-all duration-300">
                <div className="flex items-start gap-4">
                  <div className="bg-gradient-to-br from-blue-500 to-blue-600 p-3 rounded-xl text-white font-light text-lg min-w-[48px] h-12 flex items-center justify-center shadow-lg">
                    3
                  </div>
                  <div className="flex-grow">
                    <div className="flex items-center gap-3 mb-3">
                      <Hammer className="h-6 w-6 text-blue-400" />
                      <h4 className="text-xl font-light text-white tracking-wide">Premium Auction Placement</h4>
                    </div>
                    <p className="text-white/70 leading-relaxed font-light">
                      Your artwork gets featured in our live auctions with professional photography, detailed descriptions, and targeted marketing to serious collectors.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Enhanced Call-to-Action */}
          <div className="text-center">
            <div className="bg-white/5 backdrop-blur-xl rounded-3xl shadow-2xl border border-white/10 p-8 max-w-3xl mx-auto">
              <h3 className="text-3xl font-light text-white mb-4 tracking-wide">
                Ready to Consign Your Highwaymen Art?
              </h3>
              <p className="text-lg text-white/70 mb-8 leading-relaxed font-light">
                Join hundreds of satisfied consigners who have trusted us with their valuable Highwaymen artwork. 
                Get your free evaluation today and discover your artwork's true market value.
              </p>

              <div className="flex flex-col sm:flex-row gap-4 justify-center items-center mb-6">
            <Link to="/consign">
                  <Button size="lg" className="bg-gradient-to-r from-emerald-600 to-teal-600 hover:from-emerald-500 hover:to-teal-500 text-white font-light px-8 py-4 shadow-lg hover:shadow-xl transition-all duration-300 border-0 backdrop-blur-xl tracking-wide hover:scale-[1.02] hover:-translate-y-0.5 rounded-xl">
                    <PackageOpen className="mr-3 h-5 w-5" />
                    Start Your Consignment
                    <ArrowRight className="ml-3 h-5 w-5" />
              </Button>
            </Link>
                <span className="text-white/50">or</span>
                <a href="tel:+1234567890" className="text-emerald-400 hover:text-emerald-300 font-light transition-colors tracking-wide">
                  Call us at (555) 123-4567
                </a>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-6 text-sm text-white/60">
                <div className="flex items-center justify-center gap-2">
                  <Shield className="h-4 w-4 text-emerald-400" />
                  <span className="font-light">Fully Insured Process</span>
                </div>
                <div className="flex items-center justify-center gap-2">
                  <Clock className="h-4 w-4 text-blue-400" />
                  <span className="font-light">48-Hour Response</span>
                </div>
                <div className="flex items-center justify-center gap-2">
                  <Award className="h-4 w-4 text-teal-400" />
                  <span className="font-light">Expert Authentication</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* About Section with rich content */}
      <section className="py-20 bg-gradient-to-br from-slate-950 via-slate-900 to-slate-950 relative overflow-hidden">
        {/* Professional Background Effects */}
        <div className="absolute inset-0">
          <div className="absolute top-0 left-1/4 w-96 h-96 bg-emerald-500/5 rounded-full blur-3xl" />
          <div className="absolute bottom-0 right-1/4 w-96 h-96 bg-blue-500/5 rounded-full blur-3xl" />
          <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] bg-teal-500/3 rounded-full blur-3xl" />
            </div>
            
        {/* Subtle grid overlay */}
        <div 
          className="absolute inset-0 opacity-[0.02]"
          style={{
            backgroundImage: `url("data:image/svg+xml,%3Csvg width='32' height='32' viewBox='0 0 32 32' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='%23ffffff' fill-opacity='0.05'%3E%3Cpath d='M0 0h1v1H0zM16 16h1v1h-1z'/%3E%3C/g%3E%3C/svg%3E")`,
            backgroundSize: '32px 32px'
          }}
        />

        <div className="container mx-auto px-4 relative z-10">
          <div className="max-w-4xl mx-auto">
            <div className="text-center mb-16">
              <div className="inline-flex items-center justify-center px-8 py-3 mb-8 bg-white/5 backdrop-blur-xl rounded-full border border-white/10 shadow-lg">
                <Palette className="h-5 w-5 mr-3 text-emerald-400" />
                <span className="text-white font-light tracking-wide">The Legacy</span>
              </div>
              <h2 className="text-5xl md:text-6xl lg:text-7xl font-extralight mb-8 text-white tracking-tight leading-tight">
                <span className="block">About Florida</span>
                <span className="block bg-gradient-to-r from-emerald-400 via-teal-400 to-blue-400 bg-clip-text text-transparent font-light">Highwaymen</span>
              </h2>
              <div className="w-24 h-px bg-gradient-to-r from-transparent via-emerald-400 to-transparent mx-auto" />
            </div>
            
            <div className="bg-white/5 backdrop-blur-xl rounded-3xl border border-white/10 shadow-2xl p-8 md:p-12">
              <p className="text-xl text-white/80 leading-relaxed mb-8 font-light">
                The Florida Highwaymen are a group of 26 African American landscape artists who have been called 
                <span className="font-normal text-emerald-300 italic"> "The Last Great American Art Movement of the 20th century."</span> 
                Self-taught artists who created idyllic, quickly realized images of the Florida landscape and peddled 
                them door to door to businesses and individuals throughout the state from the mid-1950s through the 1980s.
              </p>
              
              <div className="my-8 bg-white/5 backdrop-blur-xl rounded-2xl p-6 border border-white/10 border-l-4 border-l-emerald-400">
                <p className="font-light text-white/90 italic text-lg leading-relaxed">
                  "Their work has become increasingly collectible since the mid-1990s, with paintings that originally 
                  sold for $25 now bringing thousands of dollars."
                </p>
              </div>
              
              <p className="text-white/70 leading-relaxed text-lg font-light mb-10">
                Our auction platform offers authenticated Highwaymen artwork to collectors and enthusiasts, 
                ensuring the legacy of these remarkable artists continues to be celebrated and preserved for future generations.
              </p>
              
              <div className="flex flex-col sm:flex-row gap-6 justify-center">
                <Link to="/auctions">
                  <Button className="bg-gradient-to-r from-emerald-600 to-teal-600 hover:from-emerald-500 hover:to-teal-500 text-white px-10 py-4 text-lg font-light shadow-lg hover:shadow-xl transition-all duration-300 border-0 backdrop-blur-xl tracking-wide hover:scale-[1.02] hover:-translate-y-0.5 rounded-xl">
                    Browse Current Auctions
                  </Button>
                </Link>
                <Link to="/artworks">
                  <Button variant="ghost" className="border border-white/20 text-white/80 hover:text-white hover:bg-white/10 px-10 py-4 text-lg font-light transition-all rounded-xl shadow-lg hover:shadow-xl bg-white/5 backdrop-blur-xl hover:scale-[1.02] hover:-translate-y-0.5">
                    Explore the Collection
                  </Button>
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>



      {/* Professional Consignment Section */}
      <section className="relative py-20 md:py-28 overflow-hidden bg-gradient-to-br from-slate-900 via-slate-950 to-slate-900">
        {/* Sophisticated Background Effects */}
        <div className="absolute inset-0">
          <div className="absolute top-1/4 left-0 w-96 h-96 bg-emerald-500/3 rounded-full blur-3xl" />
          <div className="absolute bottom-1/4 right-0 w-96 h-96 bg-blue-500/3 rounded-full blur-3xl" />
        </div>

        <div className="container mx-auto px-4 relative z-10">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 mb-16">
            {/* Professional Visual */}
            <div className="relative">
              <div className="group relative h-full min-h-[500px] rounded-3xl overflow-hidden shadow-2xl border border-white/10">
                <div className="absolute inset-0 bg-gradient-to-br from-emerald-600/90 to-teal-700/90 backdrop-blur-sm"></div>
                <div className="absolute inset-0 opacity-20">
                  <img 
                    src="https://p1.liveauctioneers.com/9289/372549/208419151_1_x.jpg?quality=80&sharpen=true&version=1749153407"
                    alt="Highwaymen Artwork Consignment"
                    className="w-full h-full object-cover"
                  />
                </div>
                
                {/* Professional Content Overlay */}
                <div className="relative z-10 p-12 h-full flex flex-col justify-center text-white">
                  <div className="bg-white/10 backdrop-blur-xl rounded-2xl p-8 border border-white/20 shadow-2xl">
                    <div className="w-16 h-16 rounded-xl bg-white/20 backdrop-blur-sm flex items-center justify-center mb-6 border border-white/30">
                      <PackageOpen className="h-8 w-8 text-white" />
                    </div>
                    <h3 className="text-3xl font-light mb-4 leading-tight">
                      Professional <span className="font-normal">Consignment</span>
                    </h3>
                    <p className="text-lg text-white/90 mb-6 leading-relaxed font-light">
                      Our expert team provides comprehensive consignment services for Florida Highwaymen artwork, 
                      ensuring maximum value and professional presentation.
                    </p>
                    <div className="space-y-3">
                      <div className="flex items-center space-x-3">
                        <div className="w-2 h-2 bg-white rounded-full"></div>
                        <span className="text-white/90 font-light">Expert Authentication & Evaluation</span>
                      </div>
                      <div className="flex items-center space-x-3">
                        <div className="w-2 h-2 bg-white rounded-full"></div>
                        <span className="text-white/90 font-light">Professional Photography & Documentation</span>
                      </div>
                      <div className="flex items-center space-x-3">
                        <div className="w-2 h-2 bg-white rounded-full"></div>
                        <span className="text-white/90 font-light">Strategic Marketing & Placement</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            
            {/* Professional Information */}
            <div className="flex flex-col justify-center space-y-8">
              <div>
                <div className="inline-flex items-center justify-center px-8 py-3 mb-8 bg-white/5 backdrop-blur-xl rounded-full border border-white/10 shadow-xl">
                  <TrendingUp className="h-5 w-5 mr-3 text-emerald-400" />
                  <span className="text-white/90 font-light tracking-wider uppercase text-sm">Consignment Services</span>
                </div>
                
                <h2 className="text-4xl md:text-5xl lg:text-6xl font-light mb-6 text-white tracking-tight leading-[1.1]">
                  <span className="block font-extralight">Maximize Your</span>
                  <span className="block bg-gradient-to-r from-emerald-400 via-teal-300 to-green-400 bg-clip-text text-transparent font-normal">Artwork Value</span>
                </h2>
                
                <p className="text-xl text-white/70 leading-relaxed font-light mb-8">
                  Join hundreds of satisfied consigners who have entrusted their valuable Highwaymen artwork to our expert team.
                </p>
              </div>
              
              {/* Professional Process */}
              <div className="space-y-6">
                {[
                  {
                    step: "01",
                    title: "Free Evaluation",
                    description: "Submit photos for expert assessment and market valuation"
                  },
                  {
                    step: "02", 
                    title: "Professional Documentation",
                    description: "High-quality photography and detailed cataloging"
                  },
                  {
                    step: "03",
                    title: "Strategic Auction Placement",
                    description: "Optimal timing and positioning for maximum results"
                  },
                  {
                    step: "04",
                    title: "Settlement & Support",
                    description: "Transparent accounting and ongoing communication"
                  }
                ].map((item, index) => (
                  <div key={index} className="flex items-start space-x-4">
                    <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-emerald-500/20 to-teal-600/20 backdrop-blur-sm flex items-center justify-center border border-emerald-400/30 flex-shrink-0">
                      <span className="text-emerald-300 font-medium">{item.step}</span>
                    </div>
                    <div>
                      <h4 className="text-lg font-medium text-white mb-2">{item.title}</h4>
                      <p className="text-white/70 font-light leading-relaxed">{item.description}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
          
          {/* Professional CTA */}
          <div className="text-center">
            <div className="bg-white/5 backdrop-blur-xl rounded-3xl shadow-2xl border border-white/10 p-12 max-w-4xl mx-auto">
              <h3 className="text-3xl font-light text-white mb-4">
                Ready to Consign Your Highwaymen Art?
              </h3>
              <p className="text-lg text-white/80 mb-8 leading-relaxed font-light">
                Get your free evaluation today and discover your artwork's true market value.
              </p>

              <div className="flex flex-col sm:flex-row gap-6 justify-center items-center">
                <Link to="/consign">
                  <Button size="lg" className="bg-gradient-to-r from-emerald-600 to-teal-600 hover:from-emerald-500 hover:to-teal-500 text-white font-medium px-8 py-4 shadow-2xl hover:shadow-emerald-500/25 transform hover:scale-[1.02] hover:-translate-y-1 transition-all rounded-xl border border-emerald-400/20">
                    <PackageOpen className="mr-3 h-5 w-5" />
                    Start Your Consignment
                    <ArrowRight className="ml-3 h-5 w-5" />
                  </Button>
                </Link>
                <a href="tel:+1234567890" className="text-white/70 hover:text-white font-light transition-all">
                  or call us at (555) 123-4567
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>



      {/* Professional Art Experience Section */}
      <section className="relative py-20 md:py-28 overflow-hidden bg-gradient-to-br from-slate-950 via-slate-900 to-slate-950">
        {/* Sophisticated Background Effects */}
        <div className="absolute inset-0">
          <div className="absolute top-0 left-1/3 w-96 h-96 bg-blue-500/3 rounded-full blur-3xl" />
          <div className="absolute bottom-0 right-1/3 w-96 h-96 bg-emerald-500/3 rounded-full blur-3xl" />
        </div>

        {/* Professional pattern overlay */}
        <div 
          className="absolute inset-0 opacity-[0.01]"
          style={{
            backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='1'%3E%3Ccircle cx='30' cy='30' r='2'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`,
            backgroundSize: '60px 60px'
          }}>
        </div>
        
        <div className="container mx-auto px-4 relative z-10">
          {/* Professional Header Section */}
          <div className="text-center mb-20">
            <div className="inline-flex items-center justify-center px-8 py-3 mb-8 bg-white/5 backdrop-blur-xl rounded-full border border-white/10 shadow-lg">
              <Award className="h-5 w-5 mr-3 text-blue-400" />
              <span className="text-white font-light tracking-wide">Join Our Exclusive Community</span>
            </div>
            
            <h2 className="text-5xl md:text-6xl lg:text-7xl font-extralight mb-8 text-white tracking-tight leading-tight">
              <span className="block">Experience the</span>
              <span className="block bg-gradient-to-r from-emerald-400 via-teal-400 to-blue-400 bg-clip-text text-transparent font-light">Art of Collecting</span>
            </h2>
            
            <p className="text-2xl text-white/70 max-w-5xl mx-auto leading-relaxed font-light">
              Step into Florida's most prestigious auction house where <span className="text-emerald-300 font-normal">authentic Highwaymen masterpieces</span> meet 
              modern technology, creating an unparalleled collecting experience
            </p>
          </div>

          {/* Premium Features Grid - Asymmetric Layout */}
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 mb-24">
            {/* Large Feature Card - Takes up more space */}
            <div className="lg:col-span-7">
              <div className="relative h-full min-h-[500px] group cursor-pointer">
                {/* Background artwork - Less green saturation */}
                <div className="absolute inset-0 rounded-3xl overflow-hidden shadow-2xl border border-white/10">
                  <img 
                    src="/lovable-uploads/25daf720-6e00-4ba7-9642-84ee91f0e896.png"
                    alt="Premium Art Experience"
                    className="w-full h-full object-cover transition-all duration-700 group-hover:scale-110 saturate-75"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-slate-950/90 via-slate-900/50 to-transparent"></div>
                  
                  {/* Floating elements */}
                  <div className="absolute top-8 right-8 bg-white/10 backdrop-blur-xl rounded-full p-4 shadow-xl border border-white/20">
                    <Shield className="h-8 w-8 text-white" />
                  </div>
                </div>
                
                {/* Content overlay */}
                <div className="absolute inset-0 p-10 flex flex-col justify-end text-white">
                  <div className="bg-white/5 backdrop-blur-xl rounded-full px-6 py-2 border border-white/20 shadow-lg self-start mb-6">
                    <span className="text-white font-light tracking-wide">Premium Access</span>
                  </div>
                  <h3 className="text-4xl font-light mb-4 leading-tight tracking-wide">
                    Authenticated Masterpieces
                  </h3>
                  <p className="text-xl text-white/80 mb-6 max-w-lg leading-relaxed font-light">
                    Every artwork comes with complete provenance documentation and our guarantee of authenticity. 
                    Access museum-quality pieces with full Certificate of Authenticity.
                  </p>
                  <div className="flex items-center gap-6">
                    <div className="flex items-center gap-2">
                      <div className="w-2 h-2 bg-emerald-300 rounded-full"></div>
                      <span className="text-sm font-light tracking-wide">Expert Verified</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <div className="w-2 h-2 bg-blue-300 rounded-full"></div>
                      <span className="text-sm font-light tracking-wide">Insured Shipping</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <div className="w-2 h-2 bg-teal-300 rounded-full"></div>
                      <span className="text-sm font-light tracking-wide">Lifetime Support</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Smaller Feature Cards */}
            <div className="lg:col-span-5 space-y-8">
              {/* Live Experience Card */}
              <div className="relative h-64 group cursor-pointer">
                <div className="absolute inset-0 rounded-2xl overflow-hidden shadow-xl border border-white/10">
                  <img 
                    src="/lovable-uploads/16aa0f6f-b130-43e7-b02c-dcdfd7bd0c6f.png"
                    alt="Live Auction Experience"
                    className="w-full h-full object-cover transition-all duration-500 group-hover:scale-105"
                  />
                  <div className="absolute inset-0 bg-gradient-to-r from-slate-950/80 to-slate-900/50"></div>
                  
                  <div className="absolute top-4 right-4 bg-red-500/90 backdrop-blur-sm rounded-full px-3 py-1 text-white text-sm font-light flex items-center gap-2 border border-red-400/20">
                    <div className="w-2 h-2 bg-white rounded-full animate-pulse"></div>
                    LIVE
                  </div>
                </div>
                
                <div className="absolute inset-0 p-6 flex flex-col justify-end text-white">
                  <h4 className="text-2xl font-light mb-2 tracking-wide">Live Streaming Auctions</h4>
                  <p className="text-white/80 text-sm leading-relaxed font-light">
                    Watch expert auctioneers present each piece with detailed insights. Bid in real-time from anywhere in the world.
                  </p>
                </div>
              </div>

              {/* Cost Advantage Card */}
              <div className="relative h-64 group cursor-pointer">
                <div className="absolute inset-0 rounded-2xl overflow-hidden shadow-xl border border-white/10">
                  <div className="w-full h-full bg-gradient-to-br from-emerald-600/80 via-teal-600/80 to-blue-600/80 backdrop-blur-sm"></div>
                  <div className="absolute inset-0 opacity-10">
                    <img 
                      src="/lovable-uploads/063d238d-885b-42fc-869c-cf0cf4bc890b.png"
                      alt="Cost Savings"
                      className="w-full h-full object-cover"
                    />
                  </div>
                  
                  <div className="absolute top-4 right-4 bg-emerald-500/80 backdrop-blur-sm rounded-full p-3 border border-emerald-400/20">
                    <DollarSign className="h-6 w-6 text-white" />
                  </div>
                </div>
                
                <div className="absolute inset-0 p-6 flex flex-col justify-center text-white">
                  <h4 className="text-2xl font-light mb-3 tracking-wide">Better Value</h4>
                  <p className="text-white/80 text-sm mb-4 leading-relaxed font-light">
                    No traditional auctioneer premiums. Our efficient digital platform means more artwork for your investment.
                  </p>
                  <div className="text-3xl font-light text-emerald-200 tracking-wide">Save 15-25%</div>
                </div>
              </div>
            </div>
          </div>

          {/* Professional Secondary Benefits */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-20">
            <div className="group">
              <div className="bg-white/5 backdrop-blur-xl rounded-2xl p-8 border border-white/10 hover:border-white/20 shadow-lg hover:shadow-2xl transition-all duration-500 hover:-translate-y-2 h-full">
                <div className="flex items-center justify-center w-16 h-16 bg-white/10 backdrop-blur-sm rounded-2xl mb-6 group-hover:scale-110 transition-transform duration-300">
                  <Zap className="h-8 w-8 text-emerald-300" />
                </div>
                <h4 className="text-xl font-light text-white mb-4 tracking-wide">Instant Registration</h4>
                <p className="text-white/70 leading-relaxed font-light">
                  Register and start bidding immediately. No lengthy verification processes or waiting periods.
                </p>
              </div>
            </div>

            <div className="group">
              <div className="bg-white/5 backdrop-blur-xl rounded-2xl p-8 border border-white/10 hover:border-white/20 shadow-lg hover:shadow-2xl transition-all duration-500 hover:-translate-y-2 h-full">
                <div className="flex items-center justify-center w-16 h-16 bg-white/10 backdrop-blur-sm rounded-2xl mb-6 group-hover:scale-110 transition-transform duration-300">
                  <BookOpen className="h-8 w-8 text-blue-300" />
                </div>
                <h4 className="text-xl font-light text-white mb-4 tracking-wide">Expert Curation</h4>
                <p className="text-white/70 leading-relaxed font-light">
                  Access detailed artist histories, provenance research, and expert valuations for informed collecting.
                </p>
              </div>
            </div>

            <div className="group">
              <div className="bg-white/5 backdrop-blur-xl rounded-2xl p-8 border border-white/10 hover:border-white/20 shadow-lg hover:shadow-2xl transition-all duration-500 hover:-translate-y-2 h-full">
                <div className="flex items-center justify-center w-16 h-16 bg-white/10 backdrop-blur-sm rounded-2xl mb-6 group-hover:scale-110 transition-transform duration-300">
                  <Globe className="h-8 w-8 text-teal-300" />
                </div>
                <h4 className="text-xl font-light text-white mb-4 tracking-wide">Global Community</h4>
                <p className="text-white/70 leading-relaxed font-light">
                  Join collectors worldwide in our exclusive community dedicated to preserving Highwaymen legacy.
                </p>
              </div>
            </div>
          </div>

          {/* Professional Stats Section */}
          <div className="text-center mb-20">
            <div className="grid grid-cols-1 md:grid-cols-4 gap-6 max-w-4xl mx-auto">
              <div className="bg-white/5 backdrop-blur-xl rounded-2xl p-8 shadow-xl border border-white/10 hover:border-white/20 hover:scale-105 transition-all duration-300">
                <div className="text-4xl font-extralight text-emerald-300 mb-3 tracking-wide">300+</div>
                <p className="text-white/70 font-light tracking-wide">Artworks Sold</p>
              </div>
              <div className="bg-white/5 backdrop-blur-xl rounded-2xl p-8 shadow-xl border border-white/10 hover:border-white/20 hover:scale-105 transition-all duration-300">
                <div className="text-4xl font-extralight text-blue-300 mb-3 tracking-wide">100%</div>
                <p className="text-white/70 font-light tracking-wide">Satisfaction Rate</p>
              </div>
              <div className="bg-white/5 backdrop-blur-xl rounded-2xl p-8 shadow-xl border border-white/10 hover:border-white/20 hover:scale-105 transition-all duration-300">
                <div className="text-4xl font-extralight text-teal-300 mb-3 tracking-wide">5</div>
                <p className="text-white/70 font-light tracking-wide">Live Auctions</p>
              </div>
              <div className="bg-white/5 backdrop-blur-xl rounded-2xl p-8 shadow-xl border border-white/10 hover:border-white/20 hover:scale-105 transition-all duration-300">
                <div className="text-4xl font-extralight text-emerald-300 mb-3 tracking-wide">24/7</div>
                <p className="text-white/70 font-light tracking-wide">Platform Access</p>
              </div>
            </div>
          </div>

          {/* Professional Call to Action */}
          <div className="text-center">
            <div className="max-w-4xl mx-auto">
              <div className="relative bg-gradient-to-r from-emerald-600/80 via-teal-600/80 to-blue-600/80 backdrop-blur-xl rounded-3xl p-12 shadow-2xl border border-white/20 overflow-hidden">
                {/* Glass morphism overlay */}
                <div className="absolute inset-0 bg-white/5 backdrop-blur-sm" />
                
                {/* Decorative background pattern - reduced opacity */}
                <div className="absolute inset-0 opacity-5">
                  <img 
                    src="/lovable-uploads/e5c83769-43dc-4636-8314-a6708fdda351.png"
                    alt="Background pattern"
                    className="w-full h-full object-cover"
                  />
                </div>
                
                <div className="relative z-10">
                  <h3 className="text-4xl md:text-5xl font-extralight text-white mb-6 leading-tight tracking-wide">
                    Start Your Collection Today
                  </h3>
                  <p className="text-xl text-white/80 mb-10 max-w-2xl mx-auto leading-relaxed font-light">
                    Join thousands of collectors who trust us for authentic Florida Highwaymen artwork. 
                    Registration is free and gives you immediate access to our entire platform.
                  </p>
                  
                  <div className="flex flex-col sm:flex-row gap-6 justify-center items-center">
                    <Link to="/login">
                      <Button className="bg-white text-slate-900 hover:bg-white/90 px-12 py-6 text-xl font-light shadow-xl hover:shadow-2xl transition-all transform hover:scale-105 rounded-2xl border border-white/20">
                        Register Free <ArrowRight className="ml-3 h-6 w-6" />
                      </Button>
                    </Link>
                    <Button variant="outline" className="bg-white/5 text-white border-2 border-white/30 hover:bg-white/10 hover:text-white px-12 py-6 text-xl font-light transition-all transform hover:scale-105 rounded-2xl backdrop-blur-sm">
                      Explore Gallery
                    </Button>
                  </div>
                  
                  <div className="mt-8 flex flex-wrap justify-center items-center gap-8 text-white/60 font-light">
                    <span>✓ Free Registration</span>
                    <span>✓ Instant Access</span>
                    <span>✓ Expert Support</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Featured Blog Posts */}
      {featuredBlogPosts.length > 0 && (
        <section className="py-20 bg-gradient-to-br from-slate-900 via-slate-950 to-slate-900">
          <div className="container mx-auto px-4">
            {/* Professional Header */}
            <div className="text-center mb-16">
              <div className="inline-flex items-center justify-center px-8 py-3 mb-8 bg-white/5 backdrop-blur-xl rounded-full border border-white/10 shadow-xl">
                <BookOpen className="h-5 w-5 mr-3 text-blue-400" />
                <span className="text-white/90 font-light tracking-wider uppercase text-sm">Latest Stories</span>
              </div>
              
              <h2 className="text-4xl md:text-5xl lg:text-6xl font-light mb-6 text-white tracking-tight leading-[1.1]">
                <span className="block font-extralight">Art</span>
                <span className="block bg-gradient-to-r from-blue-400 via-indigo-300 to-blue-400 bg-clip-text text-transparent font-normal">Stories</span>
              </h2>
              
              <p className="text-xl text-white/70 max-w-3xl mx-auto leading-relaxed font-light">
                Discover the fascinating stories behind Florida's most celebrated artists and their masterpieces
              </p>
            </div>

            <div className="text-center mt-10">
              <Link to="/blog">
                <Button variant="ghost" className="text-white/80 hover:text-white border border-white/20 hover:bg-white/10 px-8 py-3 rounded-xl backdrop-blur-xl">
                  Read More Stories <ArrowRight className="ml-2 h-4 w-4" />
                </Button>
              </Link>
            </div>
          </div>
        </section>
      )}

      {/* Professional Featured Blog Posts */}
      {featuredBlogPosts.length > 0 && (
        <section className="relative py-20 md:py-28 overflow-hidden bg-gradient-to-br from-emerald-900 via-teal-800 to-blue-900">
          {/* Sophisticated Background Effects */}
          <div className="absolute inset-0">
            <div className="absolute top-1/3 left-0 w-96 h-96 bg-blue-500/3 rounded-full blur-3xl" />
            <div className="absolute bottom-1/3 right-0 w-96 h-96 bg-emerald-500/3 rounded-full blur-3xl" />
          </div>

          {/* Professional pattern overlay */}
          <div 
            className="absolute inset-0 opacity-[0.01]"
            style={{
              backgroundImage: `url("data:image/svg+xml,%3Csvg width='40' height='40' viewBox='0 0 40 40' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='1'%3E%3Ccircle cx='20' cy='20' r='1'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`,
              backgroundSize: '40px 40px'
            }}>
          </div>

          <div className="container mx-auto px-4 relative z-10">
            <div className="flex justify-between items-center mb-12">
              <div>
                <div className="inline-flex items-center justify-center px-6 py-2 mb-4 bg-white/5 backdrop-blur-xl rounded-full border border-white/10 shadow-lg">
                  <span className="text-teal-400 font-light tracking-wide">Latest Stories</span>
                </div>
                <h2 className="text-4xl md:text-5xl font-extralight text-white mb-4 tracking-wide">Featured Blog Posts</h2>
                <p className="text-white/70 font-light">
                  Dive deeper into the world of Florida Highwaymen art
                </p>
              </div>
              <Link to="/blog" className="flex items-center text-emerald-300 hover:text-emerald-200 font-light tracking-wide transition-colors">
                View all posts <ChevronRight className="h-4 w-4 ml-1" />
              </Link>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {featuredBlogPosts.map((post) => (
                <Link to={`/blog/${post.slug}`} key={post.id} className="block group">
                  <div className="bg-white/5 backdrop-blur-xl border border-white/10 hover:border-white/20 shadow-lg group-hover:shadow-2xl transition-all duration-300 h-full flex flex-col rounded-2xl overflow-hidden hover:-translate-y-1">
                    {post.featured_image_url && (
                      <div className="relative h-48 w-full overflow-hidden">
                        <img 
                          src={post.featured_image_url} 
                          alt={post.title}
                          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                        />
                        {post.category && (
                          <div 
                            className="absolute top-3 left-3 px-3 py-1 rounded-full text-white text-xs font-light backdrop-blur-sm border border-white/20"
                            style={{ 
                              backgroundColor: post.category.color + "80"
                            }}
                          >
                            {post.category.name}
                          </div>
                        )}
                      </div>
                    )}
                    
                    <div className="p-6 flex-grow flex flex-col">
                      <h3 className="text-lg font-light mb-3 line-clamp-2 group-hover:text-emerald-300 transition-colors text-white tracking-wide">
                        {post.title}
                      </h3>
                      
                      <p className="text-white/70 mb-4 line-clamp-3 flex-grow font-light leading-relaxed">
                        {post.excerpt}
                      </p>

                      <div className="flex items-center justify-between text-sm text-white/50 font-light">
                        <div className="flex items-center gap-3">
                          {post.author && (
                            <span className="flex items-center gap-1">
                              <User className="h-3 w-3" />
                              {post.author.first_name} {post.author.last_name}
                            </span>
                          )}
                          
                          <span className="flex items-center gap-1">
                            <Clock className="h-3 w-3" />
                            {post.reading_time_minutes} min
                          </span>
                        </div>

                        <span className="flex items-center gap-1">
                          <Calendar className="h-3 w-3" />
                          {new Date(post.published_at).toLocaleDateString('en-US', { 
                            month: 'short', 
                            day: 'numeric'
                          })}
                        </span>
                      </div>
                    </div>
                  </div>
                </Link>
              ))}
            </div>

            <div className="text-center mt-12">
              <Link to="/blog">
                <Button variant="outline" className="border-white/20 text-white/80 hover:bg-white/10 px-8 py-3 rounded-xl font-light tracking-wide backdrop-blur-sm hover:border-white/40 transition-all duration-300">
                  Read More Stories <ArrowRight className="ml-2 h-4 w-4" />
                </Button>
              </Link>
            </div>
          </div>
        </section>
      )}
    </Layout>
  );
};

export default Index;
